// AuthProvider.jsx — thin React binding over auth.js (PR ② SSO).
// Provides { user, signedIn, gate, resuming } via useAuth().

import React from 'react';
import { onAuthChange, getAuthState, initAuth, sessionResumable } from './auth';

const AuthCtx = React.createContext({ ...getAuthState(), resuming: false });

export function AuthProvider({ apiBase, children }) {
  const [state, setState] = React.useState(getAuthState);
  /**
   * ⑭i — seeded SYNCHRONOUSLY, not raised in the effect below.
   *
   * `resuming` used to start false, so the shell's first paint was the
   * dashboard; the effect then raised it, the shell swapped to BootSplash, and
   * React unmounted MainApp to do so. One page load therefore mounted the whole
   * 3.2k-line component twice and fired every bootstrap request twice — the
   * first pass signed out, since the resume had not landed yet. Measured on a
   * live run: capabilities ×2, skus ×2, order-lines ×2, before any interaction.
   *
   * Reading the predicate during render is safe because it is PURE — it looks
   * at storage and module state and starts nothing. The side effect (the actual
   * refresh) stays in the effect, where it belongs.
   */
  const [resuming, setResuming] = React.useState(sessionResumable);

  React.useEffect(() => {
    const off = onAuthChange((s) => { setState(s); setResuming(false); });
    const resumed = initAuth(apiBase);   // idempotent (StrictMode-safe)
    // Lower it if no resume is pending — a seeded `true` that never resolves
    // would hold the splash forever, which is the failure mode of deciding
    // early. `sessionResumable()` is re-read rather than trusting `resumed`
    // alone: `initAuth` returns false on a StrictMode SECOND mount because it
    // is idempotent, and lowering on that would drop the splash while the
    // resume from the first mount is still in flight.
    setResuming(resumed || sessionResumable());
    setState(getAuthState());
    return off;
  }, [apiBase]);

  const value = React.useMemo(() => ({ ...state, resuming }), [state, resuming]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() { return React.useContext(AuthCtx); }
